import { useQuery } from '@tanstack/react-query'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { getSupplierWorkforce } from './api'
import { FormSkeleton } from './form-skeleton'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'
import { useWorkforceForm } from './use-workforce-form'
import { WORKFORCE_MATRIX, WorkforceFormProvider } from './workforce-provider'

export function SupplierWorkforceForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-workforce'],
    queryFn: ({ signal }) => getSupplierWorkforce(signal)
  })

  if (isLoading) return <FormSkeleton />
  return (
    <WorkforceFormProvider
      initial={data?.workforce ?? null}
      onSuccess={onSuccess}
    >
      <FormBody submitLabel={submitLabel} />
    </WorkforceFormProvider>
  )
}

function FormBody({ submitLabel }: { submitLabel: string }) {
  const { state, derived, dispatch, isSubmitting, handleSubmit } =
    useWorkforceForm()

  return (
    <form onSubmit={handleSubmit} className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        <div className='grid gap-4 sm:grid-cols-2'>
          <NumField
            id='workforce-total'
            label='Total employees'
            required
            value={state.totalEmployees}
            onChange={v =>
              dispatch({ type: 'setField', field: 'totalEmployees', value: v })
            }
            invalid={derived.totalMissing}
          />
          <NumField
            id='workforce-corporate'
            label='Corporate staff'
            value={state.corporateStaff}
            onChange={v =>
              dispatch({ type: 'setField', field: 'corporateStaff', value: v })
            }
          />
        </div>

        <div className='mt-6'>
          <h2 className='text-[12px] font-semibold uppercase tracking-wide text-ink-soft'>
            Department breakdown
          </h2>
          <div className='mt-3 divide-y divide-border overflow-hidden rounded-xl border border-border'>
            {WORKFORCE_MATRIX.map(row => (
              <div
                key={row.label}
                className='grid gap-4 p-4 lg:grid-cols-[minmax(0,11rem)_minmax(0,26rem)_minmax(0,26rem)] lg:gap-6'
              >
                <p className='text-[13px] font-semibold text-ink'>
                  {row.label}
                </p>
                <NumField
                  id={`workforce-${row.engineersKey}`}
                  label='Engineers'
                  description={row.engineersDescription}
                  value={state[row.engineersKey]}
                  onChange={v =>
                    dispatch({
                      type: 'setField',
                      field: row.engineersKey,
                      value: v
                    })
                  }
                />
                <NumField
                  id={`workforce-${row.techniciansKey}`}
                  label='Technicians'
                  description={row.techniciansDescription}
                  value={state[row.techniciansKey]}
                  onChange={v =>
                    dispatch({
                      type: 'setField',
                      field: row.techniciansKey,
                      value: v
                    })
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {state.submitError && (
        <p className='text-sm text-red-600'>{state.submitError}</p>
      )}

      <StepFooter
        hint={
          derived.totalMissing ? 'Total employees required' : 'Ready to save'
        }
      >
        <PrimaryButton
          type='submit'
          disabled={!derived.isValid || isSubmitting}
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>
    </form>
  )
}

function NumField({
  id,
  label,
  description,
  required,
  value,
  onChange,
  invalid
}: {
  id: string
  label: string
  description?: string
  required?: boolean
  value: string
  onChange: (next: string) => void
  invalid?: boolean
}) {
  const descriptionId = description ? `${id}-description` : undefined
  return (
    <div className='flex flex-col gap-1.5'>
      <Label htmlFor={id} className='text-[12px]'>
        {label}
        {required && <span className='ml-0.5 text-red-500'>*</span>}
      </Label>
      <Input
        id={id}
        type='text'
        inputMode='numeric'
        pattern='[0-9]*'
        maxLength={7}
        placeholder='0'
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={descriptionId}
        className='h-11 sm:h-9'
      />
      {description && (
        <p id={descriptionId} className='text-[12px] text-ink-soft'>
          {description}
        </p>
      )}
    </div>
  )
}
